import React from 'react';

import { cn } from '../../lib/utils';
import { NoDataIllustration, NoDataSearchIllustration } from '../icon';
import { HeadingS } from '../typography';

export enum TableEmptyType {
	Default = 'default',
	Search = 'search',
}

const DEFAULT_EMPTY_TITLE = 'Nothing here yet';
const DEFAULT_EMPTY_DESCRIPTION = "Data will appear here once it's available.";
const SEARCH_EMPTY_TITLE = 'No results found';
const SEARCH_EMPTY_DESCRIPTION = 'Try different keywords or clear your search.';

export interface ITableEmptyProps {
	'type': TableEmptyType;
	'message'?: string;
	'description'?: string;
	'minHeightPx'?: number;
	'className'?: string;
	'data-testid'?: string;
}

interface ITableEmptyContentProps {
	Illustration: typeof NoDataIllustration;
	title: string;
	description: string;
}

function TableEmptyContent({
	Illustration,
	title,
	description,
}: Readonly<ITableEmptyContentProps>) {
	return (
		<>
			<Illustration className="h-auto w-[180px]" aria-hidden />
			<HeadingS className="text-center">{title}</HeadingS>
			{description !== '' ? (
				<p className="mt-8 text-center text-13 leading-lh4 font-normal text-text-secondary">
					{description}
				</p>
			) : null}
		</>
	);
}

function TableEmpty({
	type,
	message,
	description,
	minHeightPx,
	className,
	'data-testid': dataTestId = 'table-empty',
}: Readonly<ITableEmptyProps>) {
	let content: React.ReactNode;

	switch (type) {
		case TableEmptyType.Default:
			content = (
				<TableEmptyContent
					Illustration={NoDataIllustration}
					title={DEFAULT_EMPTY_TITLE}
					description={description ?? message ?? DEFAULT_EMPTY_DESCRIPTION}
				/>
			);
			break;
		case TableEmptyType.Search:
			content = (
				<TableEmptyContent
					Illustration={NoDataSearchIllustration}
					title={SEARCH_EMPTY_TITLE}
					description={description ?? message ?? SEARCH_EMPTY_DESCRIPTION}
				/>
			);
			break;
		default: {
			const unhandledType: never = type;
			throw new Error(`Unhandled TableEmpty type: ${unhandledType}`);
		}
	}

	return (
		<div
			className={cn('flex h-fit w-full flex-col items-center p-32', className)}
			style={minHeightPx === undefined ? undefined : { minHeight: minHeightPx }}
			data-testid={dataTestId}
			data-slot="table-empty"
			data-type={type}
		>
			{content}
		</div>
	);
}

export { TableEmpty };
